import React from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Base input classes
    const baseInputClasses = "flex h-11 w-full rounded-xl border-2 border-input bg-background px-4 py-2.5 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:border-primary/50 hover:border-primary/30 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm focus-visible:shadow-md select-text";

    // Checkbox-specific styles
    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn(
                    "h-5 w-5 rounded-md border-2 border-input bg-background text-primary transition-all duration-300 focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // Radio button-specific styles
    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn(
                    "h-5 w-5 rounded-full border-2 border-input bg-background text-primary transition-all duration-300 focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // Main input with label wrapper
    if (label) {
        return (
            <div className="w-full">
                <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                    type={type}
                    className={cn(baseInputClasses, error && "border-red-500 focus-visible:ring-red-500", className)}
                    ref={ref}
                    id={inputId}
                    style={{ userSelect: 'text', WebkitUserSelect: 'text', MozUserSelect: 'text', msUserSelect: 'text', ...props?.style }}
                    {...props}
                />
                {description && !error && (
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
                )}
                {error && (
                    <p className="mt-1.5 text-xs text-red-500">{error}</p>
                )}
            </div>
        );
    }

    // Simple input without label
    return (
        <input
            type={type}
            className={cn(baseInputClasses, error && "border-red-500 focus-visible:ring-red-500", className)}
            ref={ref}
            id={inputId}
            style={{ userSelect: 'text', WebkitUserSelect: 'text', MozUserSelect: 'text', msUserSelect: 'text', ...props?.style }}
            {...props}
        />
    );
});

Input.displayName = "Input";

export default Input;